import React, { useEffect, useRef, useState } from 'react';
import { logEvent } from '../debug/debugLog';
import { shuffled } from '../lib/random';
import { CONTENT } from './content';
import { STATIONS, playlistUrl, stepStation } from './stations';
import TrackDetails from './TrackDetails';
import { TRACKS } from './tracks';
import { PLAYER_STATE, PLAYLIST_URL, describeError, loadYouTubeApi, readTrack, startCuratedPlayback, watchUrl } from './youtube';

const BLOCKED_AFTER_MS = 4000;
const FAIL_AFTER_ERROR_MS = 6000;
const SYNC_EVERY_MS = 1000;

const MAX_QUEUE = 100;

// channel 0 is the vignettes from tracks.json (the default); the rest are the YouTube playlists
const CHANNEL_COUNT = STATIONS.length + 1;
const channelName = (channel) => (channel === 0 ? 'vignettes' : STATIONS[channel - 1].name);
const channelSize = (channel) => (channel === 0 ? TRACKS.length : STATIONS[channel - 1].videos.length);

function MusicPlayer() {
  const mountRef = useRef(null);
  const playerRef = useRef(null);
  const [channel, setChannel] = useState(0);
  const statusRef = useRef('loading');
  const [status, setStatusState] = useState('loading'); // loading | playing | paused | blocked | failed
  const [muted, setMuted] = useState(true);
  const [title, setTitle] = useState('');
  const [errorCode, setErrorCode] = useState(null);
  const [track, setTrack] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [contentMode, setContentMode] = useState(false);

  const setStatus = (next) => {
    statusRef.current = next;
    setStatusState(next);
  };

  useEffect(() => {
    let cancelled = false;
    let cancelStart = () => {};
    let blockedTimer = null;
    let failTimer = null;
    let syncTimer = null;
    let player = null;
    let skipsOnError = 0;
    const container = mountRef.current;

    // keep the track details current, re-rendering only when something changed
    const refreshTrack = (target) => {
      const next = readTrack(target);
      setTrack((previous) => (JSON.stringify(previous) === JSON.stringify(next) ? previous : next));
    };

    const STATE_NAMES = { '-1': 'unstarted', 0: 'ended', 1: 'playing', 2: 'paused', 3: 'buffering', 5: 'cued' };

    const makeHandlers = (startPlayback) => ({
      onReady: (event) => {
        logEvent('player', 'ready');
        cancelStart = startPlayback(event.target);
        // browsers refuse autoplay when the sound is on, so tell the visitor if nothing starts
        blockedTimer = setTimeout(() => {
          if (!cancelled && statusRef.current === 'loading') {
            logEvent('player', 'nothing-started-after-4s', { status: statusRef.current });
            setStatus('blocked');
          }
        }, BLOCKED_AFTER_MS);
        syncTimer = setInterval(() => {
          try {
            setMuted(event.target.isMuted());
            refreshTrack(event.target);
          } catch (error) {
            /* player is going away */
          }
        }, SYNC_EVERY_MS);
      },

      onStateChange: (event) => {
        const state = event.data;
        refreshTrack(event.target);
        logEvent('player', 'state', { state, name: STATE_NAMES[state] || 'unknown', track: readTrack(event.target) });
        if (state === PLAYER_STATE.PLAYING || state === PLAYER_STATE.BUFFERING) {
          clearTimeout(blockedTimer);
          clearTimeout(failTimer);
          skipsOnError = 0;
        }
        if (state === PLAYER_STATE.PLAYING) {
          setStatus('playing');
          setErrorCode(null);
          try {
            setTitle(event.target.getVideoData().title || '');
          } catch (error) {
            /* no title available */
          }
        } else if (state === PLAYER_STATE.PAUSED) {
          setStatus('paused');
        }
      },

      // a removed or non-embeddable video should not stop the music, but if
      // nothing plays after an error, say so (and why) instead of waiting forever
      onError: (event) => {
        logEvent('player', 'error', { code: event.data, meaning: describeError(event.data), track: readTrack(event.target) });
        setErrorCode(event.data);
        refreshTrack(event.target);
        clearTimeout(failTimer);
        failTimer = setTimeout(() => {
          if (!cancelled && statusRef.current !== 'playing') {
            logEvent('player', 'gave-up-after-error', { status: statusRef.current });
            setStatus('failed');
          }
        }, FAIL_AFTER_ERROR_MS);
        if (skipsOnError < 5) {
          skipsOnError++;
          try {
            event.target.nextVideo();
          } catch (error) {
            setStatus('failed');
          }
        } else {
          setStatus('failed');
        }
      }
    });

    // the tracks come from tracks.json, shuffled so each visit starts somewhere different;
    // a random MAX_QUEUE of them is queued because YouTube's player is unreliable with very long id lists
    const ids = shuffled(TRACKS.map((track) => track.id)).slice(0, MAX_QUEUE);
    logEvent('player', 'mode', { mode: 'track list (tracks.json)', ids });

    loadYouTubeApi()
      .then((YT) => {
        if (cancelled) return;
        const handlers = makeHandlers((target) => {
          startCuratedPlayback(target, ids);
          return () => {};
        });

        const target = document.createElement('div');
        container.appendChild(target);
        player = new YT.Player(target, {
          host: 'https://www.youtube-nocookie.com',
          width: '100%',
          height: '100%',
          playerVars: {
            mute: 1, // browsers only allow autoplay while muted
            controls: 1,
            playsinline: 1,
            rel: 0,
            origin: window.location.origin
          },
          events: handlers
        });
        logEvent('player', 'created', { host: 'https://www.youtube-nocookie.com' });
        playerRef.current = player;
      })
      .catch((error) => {
        logEvent('player', 'setup-failed', { message: error && error.message });
        if (!cancelled) setStatus('failed');
      });

    return () => {
      cancelled = true;
      cancelStart();
      clearTimeout(blockedTimer);
      clearTimeout(failTimer);
      clearInterval(syncTimer);
      try {
        player?.destroy();
      } catch (error) {
        /* already gone */
      }
      container.replaceChildren();
      playerRef.current = null;
    };
  }, []);

  const toggleSound = () => {
    const player = playerRef.current;
    if (!player) return;
    if (muted) {
      player.unMute();
      setMuted(false);
    } else {
      player.mute();
      setMuted(true);
    }
  };

  // restart playback from `ids`, showing the usual "tuning in" state
  const playIds = (ids) => {
    const player = playerRef.current;
    if (!player) return;
    setTitle('');
    setErrorCode(null);
    setStatus('loading');
    try {
      startCuratedPlayback(player, ids);
    } catch (error) {
      setStatus('failed');
    }
  };

  // a fresh random order every time, so coming back to a channel starts on a different video
  const idsForChannel = (index) => shuffled(
    (index === 0 ? TRACKS : STATIONS[index - 1].videos).map((video) => video.id)
  ).slice(0, MAX_QUEUE);

  const changeChannel = (delta) => {
    if (contentMode) return;
    const next = stepStation(channel, delta, CHANNEL_COUNT);
    logEvent('player', 'channel', { channel: next, name: channelName(next) });
    setChannel(next);
    playIds(idsForChannel(next));
  };

  // every playlist in the content pool at once, in a fresh random order; hiding goes back to the chosen playlist
  const toggleContent = () => {
    const next = !contentMode;
    logEvent('player', 'content', { on: next });
    setContentMode(next);
    playIds(next ? shuffled(CONTENT.map((video) => video.id)).slice(0, MAX_QUEUE) : idsForChannel(channel));
  };

  const nextTrack = () => {
    logEvent('player', 'next-clicked');
    playerRef.current?.nextVideo();
  };

  let failedMessage = "the player couldn't load (an ad blocker can cause this)";
  if (errorCode) {
    failedMessage = `none of the tracks could be played here: ${describeError(errorCode)} (YouTube error ${errorCode})`;
  }

  let message = {
    loading: 'tuning in...',
    playing: title ? `♪ ${title}` : '♪ now playing',
    paused: 'paused',
    blocked: 'your browser held back the music - press ▶ on the player to start',
    failed: failedMessage
  }[status];
  if (errorCode && status !== 'failed' && status !== 'playing') {
    message = `skipping a track: ${describeError(errorCode)}`;
  }

  return (
    <div className="fm-player" role="region" aria-label="Music player">
      <div className="fm-menubar" role="toolbar" aria-label="Playlist" data-player-controls>
        <div className="fm-stepbox" aria-disabled={contentMode}>
          <button type="button" onClick={() => changeChannel(-1)} disabled={contentMode} aria-label="Previous playlist">◀</button>
          <span className="fm-channel" aria-live="polite">{channelName(channel)} <small>{channel + 1}/{CHANNEL_COUNT} · {channelSize(channel)} videos</small></span>
          <button type="button" onClick={() => changeChannel(1)} disabled={contentMode} aria-label="Next playlist">▶</button>
        </div>
        <button type="button" className="fm-content-btn" onClick={toggleContent} aria-pressed={contentMode}>
          {contentMode ? 'hide content' : 'show content'}
        </button>
      </div>

      <div className="fm-player-frame">
        <div ref={mountRef} className="fm-player-mount" />
      </div>

      <p className="fm-player-status" role="status" aria-live="polite" title={message}>{message}</p>

      <div className="fm-player-controls" data-player-controls>
        <button type="button" className="ms-btn" onClick={nextTrack} disabled={status === 'failed'}>
          skip ⏭
        </button>
        <button type="button" className="ms-btn fm-sound-btn" onClick={toggleSound} disabled={status === 'failed'} aria-pressed={!muted} title={muted ? "the music starts silent: press to turn the sound on" : undefined}>
          {muted ? '🔇 unmute' : '🔊 mute'}
        </button>
        <a className="ms-btn fm-yt-btn" href={contentMode ? (track?.id ? watchUrl(track.id) : 'https://www.youtube.com/') : (channel === 0 ? PLAYLIST_URL : playlistUrl(STATIONS[channel - 1]))} target="_blank" rel="noopener noreferrer">
          YouTube ↗
        </a>
        <button type="button" className="ms-btn" onClick={() => setShowDetails((open) => !open)} aria-expanded={showDetails} disabled={status === 'failed' && !track}>
          ℹ️ details
        </button>
      </div>

      {/* nothing to show details for when the player never started */}
      {showDetails && !(status === 'failed' && !track) && (
        <div className="fm-details">
          <TrackDetails track={track} />
        </div>
      )}
    </div>
  );
}

export default MusicPlayer;
